import { LoginErrorType } from '@/data/services/login/login-service-types'
import type { Reachability } from '@/infra/http/connectivity-probe'

/**
 * Catálogo de códigos de erro exibidos ao usuário (piloto — ver
 * `docs/error-codes.md`).
 *
 * O código é o identificador que a pessoa informa ao suporte (bot) para receber
 * resposta automática. É a FONTE DA VERDADE: a tabela do Confluence é gerada a
 * partir daqui (`npm run generate-error-catalog`), nunca o contrário.
 *
 * REGRA DE EMISSÃO: o código nasce onde nasce a DECISÃO do erro.
 * - Front (este arquivo): rede, reCAPTCHA client-side, validação de formulário.
 * - Back (`error_code` no GenericResponse): regra de negócio (pagamento,
 *   matrícula, licença, SSO).
 * Por isso as faixas de prefixo são reservadas por emissor — ver
 * `ErrorCodeCategory` — para que os dois lados nunca colidam.
 */
export enum ErrorCodeCategory {
  /** Falha de transporte: sem resposta HTTP (rede/CORS/timeout/proxy). Front. */
  CONNECTION = 'CON',
  /** Validação de segurança reCAPTCHA, client-side ou reprovada pelo backend. Front. */
  RECAPTCHA = 'CAP'
}

/**
 * Códigos de conexão, indexados pelo veredicto do probe de conectividade
 * (ver connectivity-probe). São três cenários distintos — e só o front sabe
 * diferenciá-los, já que nenhuma request chegou à API.
 */
export const CONNECTION_ERROR_CODES: Record<Reachability, string> = {
  offline: 'CON-001',
  reachable: 'CON-002',
  unreachable: 'CON-003'
}

/**
 * Códigos de reCAPTCHA e de rede na tela de login, indexados pelo
 * `LoginErrorType` já resolvido por `auth-error-mapper`.
 *
 * Só entram aqui os tipos com cenário de suporte mapeado; os demais
 * (credenciais erradas, SSO obrigatório, token de reset, etc.) seguem sem
 * código no piloto — `getErrorCodeForLoginError` devolve `undefined` e a
 * mensagem é exibida sem sufixo.
 */
export const LOGIN_ERROR_CODES: Partial<Record<LoginErrorType, string>> = {
  [LoginErrorType.WRONG_RECAPTCHA]: 'CAP-001',
  [LoginErrorType.RECAPTCHA_NOT_READY]: 'CAP-002',
  [LoginErrorType.RECAPTCHA_TOKEN_FAILED]: 'CAP-003',
  [LoginErrorType.RECAPTCHA_TOKEN_EXPIRED]: 'CAP-004',
  [LoginErrorType.RECAPTCHA_TOKEN_DUPLICATE]: 'CAP-005',
  [LoginErrorType.RECAPTCHA_TOKEN_MISSING]: 'CAP-006',
  [LoginErrorType.RECAPTCHA_TOKEN_MALFORMED]: 'CAP-007',
  [LoginErrorType.RECAPTCHA_ACTION_MISMATCH]: 'CAP-008',
  [LoginErrorType.RECAPTCHA_LOW_SCORE]: 'CAP-009',
  [LoginErrorType.RECAPTCHA_BROWSER_ERROR]: 'CAP-010',
  [LoginErrorType.RECAPTCHA_TIMEOUT]: 'CAP-011',
  [LoginErrorType.NETWORK_ERROR]: 'CON-004'
}

/**
 * Busca um código no catálogo sem cair no protótipo: `LOGIN_ERROR_CODES['toString']`
 * devolveria uma Function (truthy), que a interpolação do i18n renderizaria como
 * texto no lugar do código. As chaves consultadas nem sempre são literais do enum
 * — `auth-error-mapper` também repassa mensagem livre do backend.
 */
const lookupCode = (catalog: Record<string, string>, key: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(catalog, key) ? catalog[key] : undefined

/** Código de conexão para um veredicto de alcançabilidade. */
export const getErrorCodeForReachability = (verdict: Reachability): string =>
  lookupCode(CONNECTION_ERROR_CODES, verdict) ?? CONNECTION_ERROR_CODES.unreachable

/**
 * Código para um erro de login. Aceita `string` porque `auth-error-mapper`
 * também repassa mensagens livres do backend como `errorType` — essas não têm
 * código.
 */
export const getErrorCodeForLoginError = (
  errorType: LoginErrorType | string | null
): string | undefined => (errorType ? lookupCode(LOGIN_ERROR_CODES, errorType) : undefined)
